import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { ChevronLeft } from 'react-bootstrap-icons';
import LandingNav from '../components/shared/LandingNav';
import SiteFooter from '../components/shared/SiteFooter';
import { useTheme } from '../contexts/ThemeContext';
import { AuthContext } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import { renderMarkdown } from '../utils/markdownRenderer';
import { isPrerendering } from '../utils/prerendering';
import { fetchLearnSnapshot } from '../services/staticContent';
import Seo from '../components/Seo';
import './Learn.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Fallbacks for when the backend section has no description of its own. */
const SECTION_DESCRIPTIONS = {
  quickStart:
    'Get started with PrecisionTrader: connect a broker, select an account, and place your first bracket order.',
  tradingFundamentals:
    'Build trading discipline with structured approaches to finding an edge, managing risk, and running a consistent routine.',
  softwareGuides:
    "Guides to PrecisionTrader's risk management tools, bracket orders, position sizing, journaling, and analytics.",
};

const LearnSection = () => {
  const { section } = useParams();
  const { colors } = useTheme();
  const { user } = useContext(AuthContext);
  const isMobile = useIsMobile();
  const [learnContent, setLearnContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setExpandedSubsections] = useState({});
  const [activeSection, setActiveSection] = useState('');
  const [selectedSubsection, setSelectedSubsection] = useState('');
  const sectionRefs = useRef({});

  // Map section param to section key
  const sectionMap = {
    'quickstart': 'quickStart',
    'trading-fundamentals': 'tradingFundamentals',
    'software-guides': 'softwareGuides'
  };

  const sectionKey = sectionMap[section] || 'tradingFundamentals';

  // Build-time snapshot first so crawlers get real content, then refresh live.
  useEffect(() => {
    let cancelled = false;

    const fetchLearnContent = async () => {
      const snapshot = await fetchLearnSnapshot();
      if (cancelled) return;

      const hasSnapshot = Boolean(snapshot?.content);
      if (hasSnapshot) {
        setLearnContent(snapshot.content);
        setError(null);
        setLoading(false);
      }

      if (isPrerendering() || !BACKEND_URL) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/learn/content`);
        if (!response.ok) {
          throw new Error('Failed to fetch learn content');
        }
        const data = await response.json();
        if (!data.success || !data.content) {
          throw new Error('Invalid response format');
        }
        if (!cancelled) {
          setLearnContent(data.content);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching learn content:', err);
        if (!cancelled && !hasSnapshot) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLearnContent();
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle anchor links - set selected subsection when hash is present
  useEffect(() => {
    if (!learnContent || loading) return;

    const hash = window.location.hash.replace('#', '');
    if (hash && (sectionKey === 'tradingFundamentals' || sectionKey === 'quickStart' || sectionKey === 'softwareGuides')) {
      setSelectedSubsection(hash);
      setActiveSection(hash);
    } else if (sectionKey === 'tradingFundamentals' || sectionKey === 'quickStart' || sectionKey === 'softwareGuides') {
      // Set first subsection as default if none selected
      const firstSub = learnContent[sectionKey]?.subsections?.[0];
      if (firstSub && !selectedSubsection) {
        setSelectedSubsection(firstSub.id);
        setActiveSection(firstSub.id);
      }
    }
  }, [learnContent, loading, sectionKey, selectedSubsection]);

  // Scroll to top when selected subsection changes (for all sections)
  useEffect(() => {
    if ((sectionKey === 'tradingFundamentals' || sectionKey === 'quickStart' || sectionKey === 'softwareGuides') && selectedSubsection) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedSubsection, sectionKey]);

  // No scroll tracking needed since we show one section at a time

  // Render CTA for trading fundamentals sections
  const renderSectionCTA = () => {
    return (
      <div style={{
        marginTop: '3rem',
        padding: '2rem',
        backgroundColor: colors.bgCard,
        borderRadius: '12px',
        border: `1px solid ${colors.primary}30`,
        textAlign: 'center'
      }}>
        <h3 style={{
          fontSize: isMobile ? '1.25rem' : '1.5rem',
          fontWeight: '700',
          color: colors.text,
          marginBottom: '1rem'
        }}>
          Ready to put this into practice?
        </h3>
        <p style={{
          fontSize: isMobile ? '0.95rem' : '1.1rem',
          color: colors.textMuted,
          marginBottom: '1.5rem',
          lineHeight: '1.6'
        }}>
          PrecisionTrader helps you follow your trade plan with structure and consistency. Start trading with discipline today.
        </p>
        {user ? (
          <Link
            to="/trade"
            style={{
              display: 'inline-block',
              padding: '0.75rem 2rem',
              backgroundColor: colors.primary,
              color: '#ffffff',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Go to Trading Dashboard
          </Link>
        ) : (
          <Link
            to="/register"
            style={{
              display: 'inline-block',
              padding: '0.75rem 2rem',
              background: 'linear-gradient(135deg, #3BC4BB, #24BF96)',
              color: '#ffffff',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Start Trading for Free
          </Link>
        )}
      </div>
    );
  };

  // Render content based on section
  const renderSection = () => {
    if (!learnContent) return null;

    const sectionData = learnContent[sectionKey];
    if (!sectionData) return null;

    // For all sections, render only the selected subsection
    if (sectionKey === 'tradingFundamentals' || sectionKey === 'quickStart' || sectionKey === 'softwareGuides') {
      const selectedSub = sectionData.subsections?.find(sub => sub.id === selectedSubsection) || sectionData.subsections?.[0];
      if (!selectedSub) return null;

      // Map Quick Start subsection IDs to their corresponding images
      const imageMap = {
        'connecting-broker': '/docs/onboarding-link-data-provider.png',
        'selecting-account': '/docs/onboarding-select-account.png',
        'first-bracket-trade': '/docs/onboarding-place-bracket-trade.png'
      };
      
      const imagePath = sectionKey === 'quickStart' ? imageMap[selectedSub.id] : null;

      return (
        <article
          key={selectedSub.id}
          ref={el => { if (el) sectionRefs.current[selectedSub.id] = el; }}
          id={selectedSub.id}
          style={{
            maxWidth: '800px'
          }}
        >
          {/* Article Header with visual accent */}
          <div style={{
            marginBottom: '3rem',
            paddingBottom: '2rem'
          }}>
            <h2 style={{
              fontSize: isMobile ? '2rem' : '2.5rem',
              fontWeight: '700',
              color: colors.text,
              margin: 0,
              marginBottom: '1rem',
              lineHeight: '1.2',
              letterSpacing: '-0.02em'
            }}>
              {selectedSub.title}
            </h2>
            <div style={{
              width: '100%',
              maxWidth: '800px',
              height: '3px',
              background: `linear-gradient(90deg, ${colors.primary}, ${colors.primary}60, transparent)`,
              borderRadius: '2px',
              marginTop: '1rem'
            }} />
          </div>

          {/* Render image for Quick Start sections */}
          {imagePath && (
            <div style={{
              marginBottom: '1.5rem',
              borderRadius: '8px',
              overflow: 'hidden',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bgCard
            }}>
              <img 
                src={imagePath}
                alt={selectedSub.title}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  maxWidth: '100%'
                }}
              />
            </div>
          )}

          {/* Article Content with improved typography */}
          <div style={{
            fontSize: isMobile ? '1rem' : '1.1rem',
            lineHeight: '1.8',
            color: colors.text
          }}>
            {renderMarkdown(selectedSub.content, colors, isMobile)}
          </div>

          {/* CTA Section - only for trading fundamentals */}
          {sectionKey === 'tradingFundamentals' && renderSectionCTA()}
        </article>
      );
    }

    // Fallback - should not be reached for learn sections
    return null;
  };

  // Loading state
  if (loading) {
    return (
      <div className="learn-page" style={{ minHeight: '100vh', backgroundColor: colors.bgPrimary }}>
        <LandingNav />
        <Container style={{ maxWidth: '1200px', padding: isMobile ? '1rem' : '2rem', paddingTop: isMobile ? '1rem' : '4rem', marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Spinner animation="border" style={{ color: colors.primary }} />
          </div>
        </Container>
        <SiteFooter />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="learn-page" style={{ minHeight: '100vh', backgroundColor: colors.bgPrimary }}>
        <LandingNav />
        <Container style={{ maxWidth: '1200px', padding: isMobile ? '1rem' : '2rem', paddingTop: isMobile ? '1rem' : '4rem', marginTop: '2rem' }}>
          <Alert variant="danger" style={{ backgroundColor: colors.bgCard, borderColor: colors.danger, color: colors.text }}>
            <Alert.Heading>Error Loading Content</Alert.Heading>
            <p>{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                backgroundColor: colors.primary,
                color: '#fff',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </Alert>
        </Container>
        <SiteFooter />
      </div>
    );
  }

  // No content state
  if (!learnContent || !learnContent[sectionKey]) {
    return (
      <div className="learn-page" style={{ minHeight: '100vh', backgroundColor: colors.bgPrimary }}>
        <LandingNav />
        <Container style={{ maxWidth: '1200px', padding: isMobile ? '1rem' : '2rem', paddingTop: isMobile ? '1rem' : '4rem', marginTop: '2rem' }}>
          <Alert variant="warning" style={{ backgroundColor: colors.bgCard, borderColor: colors.warning, color: colors.text }}>
            No content available for this section.
          </Alert>
        </Container>
        <SiteFooter />
      </div>
    );
  }

  const sectionData = learnContent[sectionKey];
  const sectionTitles = {
    'quickStart': 'Quick Start',
    'tradingFundamentals': 'Trading Fundamentals',
    'softwareGuides': 'Software Guides'
  };

  // Render sidebar for all sections
  const renderSidebar = () => {
    if (!learnContent) return null;

    const sectionData = learnContent[sectionKey];
    if (!sectionData?.subsections) return null;

    return (
      <aside style={{
        position: isMobile ? 'relative' : 'sticky',
        top: isMobile ? 'auto' : '100px',
        alignSelf: 'flex-start',
        width: isMobile ? '100%' : '240px',
        marginRight: isMobile ? 0 : '3rem',
        marginBottom: isMobile ? '2rem' : 0
      }}>
        <nav style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}>
          {sectionData.subsections.map((sub) => (
            <a
              key={sub.id}
              href={`#${sub.id}`}
              onClick={(e) => {
                e.preventDefault();
                setSelectedSubsection(sub.id);
                setActiveSection(sub.id);
                // Update URL without scrolling
                window.history.pushState(null, '', `#${sub.id}`);
              }}
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                color: activeSection === sub.id ? colors.primary : colors.textMuted,
                backgroundColor: activeSection === sub.id ? colors.primary + '15' : 'transparent',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: activeSection === sub.id ? '600' : '400',
                transition: 'all 0.2s',
                border: activeSection === sub.id ? `1px solid ${colors.primary}30` : '1px solid transparent'
              }}
              onMouseEnter={(e) => {
                if (activeSection !== sub.id) {
                  e.currentTarget.style.backgroundColor = colors.bgSecondary;
                  e.currentTarget.style.color = colors.text;
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== sub.id) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = colors.textMuted;
                }
              }}
            >
              {sub.title}
            </a>
          ))}
        </nav>
      </aside>
    );
  };

  return (
    <div className="learn-page" style={{ minHeight: '100vh', backgroundColor: colors.bgPrimary }}>
      <Seo
        title={`${sectionTitles[sectionKey] || sectionData.title} — Learn | PrecisionTrader`}
        description={sectionData.description || SECTION_DESCRIPTIONS[sectionKey]}
        canonicalPath={`/learn/${section}`}
      />
      <LandingNav />
      
      <Container style={{ maxWidth: '1200px', padding: isMobile ? '1rem' : '2rem', paddingTop: isMobile ? '1rem' : '4rem', marginTop: '2rem' }}>
        {/* Back Link - for all sections */}
        <Link
          to="/learn"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: colors.primary,
            textDecoration: 'none',
            marginBottom: '2rem',
            fontSize: '0.95rem',
            fontWeight: '500'
          }}
        >
          <ChevronLeft size={18} />
          Back to Learning Center
        </Link>

        {/* Page Header */}
        <div style={{ 
          marginTop: isMobile ? '2rem' : '3rem',
          marginBottom: isMobile ? '4rem' : '8rem',
          textAlign: (sectionKey === 'tradingFundamentals' || sectionKey === 'quickStart' || sectionKey === 'softwareGuides') ? 'center' : 'left'
        }}>
          <h1 style={{ 
            color: colors.text, 
            fontSize: isMobile ? '2rem' : '3rem',
            marginBottom: '0.5rem',
            fontWeight: '700',
            lineHeight: '1.1'
          }}>
            {sectionTitles[sectionKey] || sectionData.title}
          </h1>
          <p style={{ 
            color: colors.textMuted, 
            fontSize: isMobile ? '1rem' : '1.2rem',
            maxWidth: '800px',
            margin: (sectionKey === 'tradingFundamentals' || sectionKey === 'quickStart' || sectionKey === 'softwareGuides') ? (isMobile ? '0 auto 3rem auto' : '0 auto 4.5rem auto') : 0
          }}>
            {sectionKey === 'tradingFundamentals' 
              ? 'Master trading discipline and learn how to use PrecisionTrader effectively. Explore structured approaches to finding your edge, managing risk, and building consistent trading routines—all free, so you can focus on getting better.'
              : 'Learn how to use PrecisionTrader effectively and get started with your trading journey.'}
          </p>
        </div>

        {/* Content with Sidebar for all sections */}
        {(sectionKey === 'tradingFundamentals' || sectionKey === 'quickStart' || sectionKey === 'softwareGuides') ? (
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'flex-start'
          }}>
            {renderSidebar()}
            <div style={{ flex: 1 }}>
              {renderSection()}
            </div>
          </div>
        ) : (
          <div style={{ 
            maxWidth: '900px'
          }}>
            {renderSection()}
          </div>
        )}
      </Container>

      <SiteFooter />
    </div>
  );
};

export default LearnSection;
